/**
 * Step 4, Wiring: the single-line diagram as the plan set draws it (sheet E-2), editable. Select an element to
 * rename it, drag it (its wires follow on the next drawing), hide it when optional, add boxes (sub-panel, battery,
 * generator, EV charger, custom) wired to a node, re-attach or discard edits whose element is gone, or reset
 * everything. Edits are a patch keyed by the diagram's stable ids (lib/sldOverrides.ts), saved 0.5 s after the last
 * change, so they survive regeneration. Zoom with the wheel or the buttons, pan by dragging the background.
 * On a phone the diagram is view only.
 */
import { BatteryCharging, CarFront, ChevronDown, Fuel, LayoutPanelTop, Minus, Plus, RotateCcw, Square, Trash2, Undo2, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as RKeyboardEvent, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Alert, Badge, Button, Card, Field, Select, TextInput, cx } from "../components/ui";
import { useT, type Translate } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { DirIcon } from "../i18n/mirror";
import { EXTRA_KINDS, MAX_EXTRAS, editCount, isEdited, offsetDelta, parseOrphan, type ExtraKind, type SldExtra } from "../lib/sldOverrides";
import { StepChecks, StepHead, useFocusAnchor, type Ws } from "./PermitWorkspace";
import { WiringSchedule } from "./WiringSchedule";

const KIND_ICON: Record<ExtraKind, ReactNode> = {
  sub_panel: <LayoutPanelTop size={15} />,
  battery: <BatteryCharging size={15} />,
  generator: <Fuel size={15} />,
  ev_charger: <CarFront size={15} />,
  custom: <Square size={15} />,
};

/** The dashboard's names of the extra box kinds (lib/sldOverrides EXTRA_KINDS' `label`). */
const KIND_LABEL: Record<ExtraKind, MessageKey> = {
  sub_panel: "permit.sld.kind.subPanel",
  battery: "permit.sld.kind.battery",
  generator: "permit.sld.kind.generator",
  ev_charger: "permit.sld.kind.evCharger",
  custom: "permit.sld.kind.custom",
};

/** The dashboard's names of the diagram's fixed elements (lib/sldOverrides `NAMES`, same ids). */
const ELEMENT_LABEL: Record<string, MessageKey> = {
  title: "permit.sld.element.title",
  subtitle: "permit.sld.element.subtitle",
  "array.label": "permit.sld.element.arrayLabel",
  "array.more": "permit.sld.element.arrayMore",
  inverter: "permit.sld.element.inverter",
  combiner: "permit.sld.element.combiner",
  ac_disconnect: "permit.sld.element.acDisconnect",
  msp: "permit.sld.element.msp",
  meter: "permit.sld.element.meter",
  "meter.label": "permit.sld.element.meterLabel",
  "meter.sub": "permit.sld.element.meterSub",
  "utility.label": "permit.sld.element.utilityLabel",
  "wire.array.label": "permit.sld.element.wireArrayLabel",
  "wire.ac.label": "permit.sld.element.wireAcLabel",
  "note.rapid_shutdown": "permit.sld.element.noteRapidShutdown",
  "note.temperatures": "permit.sld.element.noteTemperatures",
  "note.conductors": "permit.sld.element.noteConductors",
  "legend.dc": "permit.sld.element.legendDc",
  "legend.ac": "permit.sld.element.legendAc",
  "note.grounding": "permit.sld.element.noteGrounding",
  "note.code": "permit.sld.element.noteCode",
  "msp.gec": "permit.sld.element.mspGec",
  meter_main: "permit.sld.element.meterMain",
  "meter_main.label": "permit.sld.element.meterMainLabel",
  "tap.label": "permit.sld.element.tapLabel",
};

/** A person's name for a diagram element id (as lib/sldOverrides `elementName`, translated). */
function elementLabel(t: Translate, id: string, extras: SldExtra[] = []): string {
  const key = ELEMENT_LABEL[id];
  if (key) return t(key);
  const array = /^array\.([SB])(\d+)$/.exec(id);
  if (array) return t(array[1] === "S" ? "permit.sld.element.string" : "permit.sld.element.branch", { id: `${array[1]}${array[2]}` });
  const inverter = /^inverter\.(\d+)$/.exec(id);
  if (inverter) return t("permit.sld.element.inverterN", { n: inverter[1]! });
  if (id.startsWith("extra.")) {
    const e = extras.find((x) => `extra.${x.id}` === id);
    return e ? e.name : t("permit.sld.element.addedBox");
  }
  return id;
}

/** Where an extra box may connect, by node id (lib/sldOverrides `nodeName`, translated). */
function nodeLabel(t: Translate, node: string): string {
  return node === "array" ? t("permit.sld.element.array") : elementLabel(t, node);
}

export function WiringStep({ ws }: { ws: Ws }) {
  const t = useT();
  const [sel, setSel] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const elements = useMemo(() => ws.sld?.elements ?? [], [ws.sld]);
  // the API lists the generated elements; the added boxes are the edits' own
  const extrasList = ws.overrides.extras;
  const byId = useMemo(
    () => new Map([...elements.map((e) => [e.id, e] as const), ...extrasList.map((x) => [`extra.${x.id}`, { id: `extra.${x.id}`, kind: "extra" as const, text: x.name, optional: false }] as const)]),
    [elements, extrasList],
  );
  const editable = ws.canEdit && !ws.phone;
  const o = ws.overrides;
  const orphans = ws.sld?.orphaned ?? [];
  useFocusAnchor(ws.focus, (anchor) => {
    if (anchor.startsWith("sld:")) setSel(anchor.slice(4));
  });
  useEffect(() => {
    ws.sldKeys.current = editable
      ? {
          rename: () => {
            if (sel) window.setTimeout(() => textRef.current?.focus(), 0);
          },
          reset: () => sel && ws.dispatch({ type: "resetElement", id: sel }),
        }
      : null;
    return () => {
      ws.sldKeys.current = null;
    };
  });

  const svg = ws.preview.data?.sld_svg ?? null;
  const pendingExtra = sel?.startsWith("extra.") ? ws.overrides.extras.find((x) => `extra.${x.id}` === sel) : undefined;
  // a box just added is not in the server's element list until its save returns
  const selected = sel ? byId.get(sel) ?? (pendingExtra ? { id: sel, kind: "extra" as const, text: pendingExtra.name, optional: false } : undefined) : undefined;
  // the text as drawn now (the API's element list is computed with the company defaults, the drawing with this
  // workspace's equipment); without an edit of its own, that is the generated text
  const [drawn, setDrawn] = useState<string | null>(null);
  useEffect(() => {
    if (!sel) return setDrawn(null);
    const groups = Array.from(document.querySelectorAll(`.pw-sld-host [data-sld-id="${CSS.escape(sel)}"]`));
    const lines = groups.flatMap((g) => Array.from(g.querySelectorAll("text")).map((n) => n.textContent ?? ""));
    setDrawn(lines.length ? lines.join(sel.startsWith("wire.") ? " " : "\n") : null);
  }, [sel, svg]);
  const generatedText = selected && ws.rendered.labels[selected.id] == null && drawn != null ? drawn : selected?.text ?? "";
  const count = editCount(o);
  const [confirmReset, setConfirmReset] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const optional = elements.filter((e) => e.optional);
  const extra = sel?.startsWith("extra.") ? o.extras.find((x) => `extra.${x.id}` === sel) : undefined;
  const nodes = ws.sld?.nodes ?? [];

  const add = (kind: ExtraKind) => {
    const def = EXTRA_KINDS.find((k) => k.kind === kind)!;
    const connect = nodes.includes(def.connect) ? def.connect : nodes[0] ?? "msp";
    let id = kind.replace(/_/g, "-");
    for (let n = 1; ; n += 1) if (!o.extras.some((x) => x.id === `${id}-${n}`)) { id = `${id}-${n}`; break; }
    ws.dispatch({ type: "addExtra", kind, connect_to: connect, id });
    setSel(`extra.${id}`);
    setAddOpen(false);
  };

  return (
    <div className="pw-step-body">
      <StepHead title={t("permit.step.wiring")} description={t("permit.wiringStep.description")} status={ws.statuses.wiring} save={editable ? ws.sldSave : undefined}>
        {count > 0 && <Badge tone="info">{t("permit.wiringStep.manualEdits", { count })}</Badge>}
      </StepHead>

      {orphans.length > 0 && (
        <div data-anchor="orphans" className="pw-anchor">
          <Alert tone="warn" title={t("permit.sld.orphans.title", { count: orphans.length })}>
            {t("permit.sld.orphans.body")}
            <ul className="pw-orphans">
              {orphans.map((key) => (
                <Orphan key={key} k={key} ws={ws} elements={elements} nodes={nodes} />
              ))}
            </ul>
          </Alert>
        </div>
      )}

      <Card pad={false} className="pw-sld-card">
        <div className="pw-sld-toolbar">
          {editable && (
            <div className="pw-add">
              <Button size="sm" icon={<Plus size={14} />} onClick={() => setAddOpen((v) => !v)} disabled={o.extras.length >= MAX_EXTRAS} aria-haspopup="menu" aria-expanded={addOpen}>
                {t("permit.sld.addEquipment")} <ChevronDown size={13} />
              </Button>
              {addOpen && (
                <div className="pw-add-menu" role="menu">
                  {EXTRA_KINDS.map((k) => (
                    <button key={k.kind} role="menuitem" onClick={() => add(k.kind)}>
                      {KIND_ICON[k.kind]} {t(KIND_LABEL[k.kind])}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {editable && count > 0 && (
            confirmReset ? (
              <span className="row" style={{ gap: 6 }}>
                <span className="small">{t("permit.sld.resetAllConfirm", { count })}</span>
                <Button size="sm" variant="danger" onClick={() => { ws.dispatch({ type: "resetAll" }); setConfirmReset(false); setSel(null); }}>{t("permit.sld.reset")}</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmReset(false)}>{t("permit.sld.keep")}</Button>
              </span>
            ) : (
              <Button size="sm" variant="ghost" icon={<RotateCcw size={14} />} onClick={() => setConfirmReset(true)}>
                {t("permit.sld.resetAll")}
              </Button>
            )
          )}
          <span className="pw-sld-hint small muted">
            {editable ? t("permit.sld.hint.edit") : ws.phone ? t("permit.sld.hint.phone") : t("permit.sld.hint.viewOnly")}
          </span>
        </div>
        {editable && selected && (
          <div className="pw-sld-inspector" aria-label={t("permit.sld.inspector.label")}>
            <div className="pw-insp-name">
              <span className="pw-label">{selected.kind === "extra" ? t("permit.sld.element.addedBox") : isEdited(o, selected.id) ? t("permit.sld.inspector.edited") : t("permit.sld.inspector.asGenerated")}</span>
              <b>{elementLabel(t, selected.id, o.extras)}</b>
              <span className="small muted">
                {o.offsets[selected.id] ? t("permit.sld.inspector.moved", { dx: o.offsets[selected.id]!.dx, dy: o.offsets[selected.id]!.dy }) : t("permit.sld.inspector.generatedPosition")}
              </span>
            </div>
            {extra ? (
              <div className="pw-insp-fields">
                <Field label={t("permit.sld.field.kind")}>
                  <Select<ExtraKind> value={extra.kind} onChange={(v) => ws.dispatch({ type: "updateExtra", id: extra.id, patch: { kind: v } })} options={EXTRA_KINDS.map((k) => ({ value: k.kind, label: t(KIND_LABEL[k.kind]) }))} ariaLabel={t("permit.sld.field.kind")} />
                </Field>
                <Field label={t("permit.sld.field.name")}>
                  <TextInput value={extra.name} maxLength={60} onChange={(e) => ws.dispatch({ type: "updateExtra", id: extra.id, patch: { name: e.target.value || " " } })} />
                </Field>
                <Field label={t("permit.sld.field.detail")}>
                  <TextInput value={extra.detail ?? ""} maxLength={120} placeholder={t("permit.sld.field.detailPlaceholder", { value: 13.5 })} onChange={(e) => ws.dispatch({ type: "updateExtra", id: extra.id, patch: { detail: e.target.value || null } })} />
                </Field>
                <Field label={t("permit.sld.field.connectedTo")}>
                  <Select value={extra.connect_to} onChange={(v) => ws.dispatch({ type: "updateExtra", id: extra.id, patch: { connect_to: v } })} options={nodes.map((n) => ({ value: n, label: nodeLabel(t, n) }))} ariaLabel={t("permit.sld.field.connectedTo")} />
                </Field>
              </div>
            ) : (
              <div className="pw-insp-fields text">
                <Field label={selected.kind === "box" ? t("permit.sld.field.boxText") : t("permit.sld.field.text")}>
                  <textarea
                    ref={textRef}
                    className="textarea pw-sld-text"
                    rows={selected.kind === "box" ? Math.min(5, (o.labels[selected.id] ?? generatedText).split("\n").length) : 1}
                    value={o.labels[selected.id] ?? generatedText}
                    maxLength={300}
                    onChange={(e) => ws.dispatch({ type: "label", id: selected.id, text: e.target.value, generated: generatedText })}
                  />
                </Field>
              </div>
            )}
            <div className="pw-insp-actions">
              {o.offsets[selected.id] && (
                <Button size="sm" variant="ghost" onClick={() => ws.dispatch({ type: "resetOffset", id: selected.id })}>{t("permit.sld.resetPosition")}</Button>
              )}
              {o.labels[selected.id] != null && (
                <Button size="sm" variant="ghost" icon={<DirIcon><Undo2 size={14} /></DirIcon>} onClick={() => ws.dispatch({ type: "resetLabel", id: selected.id })}>{t("permit.sld.generatedText")}</Button>
              )}
              {selected.optional && (
                <Button size="sm" onClick={() => { ws.dispatch({ type: "hide", id: selected.id, hidden: true }); setSel(null); }}>{t("permit.sld.hide")}</Button>
              )}
              {extra && (
                <Button size="sm" variant="danger" icon={<Trash2 size={14} />} onClick={() => { ws.dispatch({ type: "removeExtra", id: extra.id }); setSel(null); }}>{t("common.remove")}</Button>
              )}
              <button className="icon-btn" onClick={() => setSel(null)} aria-label={t("permit.sld.deselect")} title={t("permit.sld.deselectTitle")}><X size={16} /></button>
            </div>
          </div>
        )}
        <SldCanvas ws={ws} svg={svg} sel={sel} onSelect={setSel} editable={editable} ids={byId} />
      </Card>

      {editable && (
        <div className="pw-cols">
          <Card title={t("permit.sld.optional.title")} description={t("permit.sld.optional.description")}>
            <div className="pw-optional">
              {optional.map((e) => {
                const hidden = o.hidden.includes(e.id);
                return (
                  <label key={e.id} className="pw-check-row">
                    <input type="checkbox" checked={!hidden} onChange={(ev) => ws.dispatch({ type: "hide", id: e.id, hidden: !ev.target.checked })} />
                    <span>{elementLabel(t, e.id, o.extras)}</span>
                  </label>
                );
              })}
            </div>
          </Card>
          <Card title={t("permit.sld.extras.title")} description={t("permit.sld.extras.description")}>
            <div className="stack" style={{ gap: 6 }}>
              {o.extras.map((x) => (
                <button key={x.id} className={cx("pw-extra-row", sel === `extra.${x.id}` && "on")} onClick={() => setSel(`extra.${x.id}`)}>
                  {KIND_ICON[x.kind]} <span className="ellipsis">{x.name}{x.detail ? ` · ${x.detail}` : ""}</span> <span className="muted small">{t("permit.sld.extras.connectsTo", { node: nodeLabel(t, x.connect_to) })}</span>
                </button>
              ))}
              {!o.extras.length && <p className="small muted" style={{ margin: 0 }}>{t("permit.sld.extras.empty")}</p>}
            </div>
          </Card>
        </div>
      )}
      <WiringSchedule ws={ws} />
      <StepChecks ws={ws} step="wiring" />
    </div>
  );
}

/** The drawing's height in diagram units (it is 1000 wide; the height follows the content), plus a margin. */
function ratioHeight(svg: string | null): number {
  const m = svg ? /viewBox="0 0 1000 ([\d.]+)"/.exec(svg) : null;
  return (m ? Number(m[1]) : 520) + 30;
}

function Orphan({ k, ws, elements, nodes }: { k: string; ws: Ws; elements: { id: string; kind: string }[]; nodes: string[] }) {
  const t = useT();
  const p = parseOrphan(k);
  const [to, setTo] = useState("");
  if (!p) return null;
  const what: MessageKey = p.kind === "labels" ? "permit.sld.orphan.labels" : p.kind === "offsets" ? "permit.sld.orphan.offsets" : p.kind === "hidden" ? "permit.sld.orphan.hidden" : "permit.sld.orphan.extras";
  const name = p.kind === "extras" ? ws.overrides.extras.find((x) => x.id === p.id)?.name ?? p.id : elementLabel(t, p.id);
  const options = p.kind === "extras" ? nodes.map((n) => ({ value: n, label: nodeLabel(t, n) })) : elements.filter((e) => e.kind !== "extra").map((e) => ({ value: e.id, label: elementLabel(t, e.id) }));
  return (
    <li>
      <span>
        <FormattedMessage id={what} values={{ name, b: (c: ReactNode[]) => <b>{c}</b> }} />
      </span>
      {ws.canEdit && (
        <span className="row" style={{ gap: 6 }}>
          <Select value={to} onChange={setTo} options={[{ value: "", label: p.kind === "extras" ? t("permit.sld.orphan.connectTo") : t("permit.sld.orphan.reattachTo") }, ...options]} ariaLabel={t("permit.sld.orphan.reattachToLabel")} />
          <Button size="sm" disabled={!to} onClick={() => ws.dispatch({ type: "reattachOrphan", key: k, to })}>{t("permit.sld.orphan.reattach")}</Button>
          <Button size="sm" variant="ghost" onClick={() => ws.dispatch({ type: "discardOrphan", key: k })}>{t("permit.sld.orphan.discard")}</Button>
        </span>
      )}
    </li>
  );
}

/** The drawing: the API's SVG (its elements carry `data-sld-id`), moved on screen by the offsets not drawn yet. */
function SldCanvas({ ws, svg, sel, onSelect, editable, ids }: { ws: Ws; svg: string | null; sel: string | null; onSelect: (id: string | null) => void; editable: boolean; ids: Map<string, { id: string }> }) {
  const t = useT();
  const stageRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ z: 1, x: 0, y: 0 });
  const drag = useRef<{ kind: "el" | "pan"; id?: string; sx: number; sy: number; base: { dx: number; dy: number }; view: typeof view; moved: boolean } | null>(null);
  const o = ws.overrides;
  const rendered = ws.rendered;

  // the SVG string replaces the drawing only when it changes (React never touches it otherwise)
  useLayoutEffect(() => {
    if (hostRef.current && svg != null && hostRef.current.dataset.sig !== String(svg.length) + svg.slice(-64)) {
      hostRef.current.innerHTML = svg;
      hostRef.current.dataset.sig = String(svg.length) + svg.slice(-64);
    }
  }, [svg]);

  const groups = useCallback((id: string) => Array.from(hostRef.current?.querySelectorAll<SVGGElement>(`[data-sld-id="${CSS.escape(id)}"]`) ?? []), []);
  const place = useCallback(
    (id: string, extra = { dx: 0, dy: 0 }) => {
      const d = offsetDelta(o, rendered, id);
      const dx = d.dx + extra.dx;
      const dy = d.dy + extra.dy;
      for (const g of groups(id)) {
        if (dx || dy) g.setAttribute("transform", `translate(${dx} ${dy})`);
        else g.removeAttribute("transform");
      }
    },
    [o, rendered, groups],
  );

  // decorate: editable elements, edited ones, the selection's outline, and offsets not drawn yet
  useLayoutEffect(() => {
    const host = hostRef.current;
    const root = host?.querySelector("svg");
    if (!host || !root) return;
    root.querySelectorAll(".sld-sel").forEach((n) => n.remove());
    for (const g of Array.from(host.querySelectorAll<SVGGElement>("[data-sld-id]"))) {
      const id = g.dataset.sldId!;
      g.classList.toggle("sld-el", ids.has(id) && editable);
      g.classList.toggle("sld-edited", isEdited(o, id));
      g.classList.toggle("sld-on", id === sel);
    }
    for (const id of ids.keys()) place(id);
    if (sel) {
      const gs = groups(sel);
      if (gs.length) {
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
        for (const g of gs) {
          const b = g.getBBox();
          x0 = Math.min(x0, b.x); y0 = Math.min(y0, b.y); x1 = Math.max(x1, b.x + b.width); y1 = Math.max(y1, b.y + b.height);
        }
        const d = offsetDelta(o, rendered, sel);
        const r = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        r.setAttribute("class", "sld-sel");
        r.setAttribute("x", String(x0 - 4 + d.dx));
        r.setAttribute("y", String(y0 - 4 + d.dy));
        r.setAttribute("width", String(x1 - x0 + 8));
        r.setAttribute("height", String(y1 - y0 + 8));
        r.setAttribute("rx", "4");
        root.appendChild(r);
      }
    }
  }, [svg, o, rendered, sel, ids, editable, place, groups]);

  // scale: screen pixels per diagram unit (the SVG is 1000 wide)
  const unit = () => (hostRef.current?.querySelector("svg")?.getBoundingClientRect().width ?? 1000) / 1000;
  // two fingers pinch to zoom (phones and tablets)
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ d: number; view: typeof view } | null>(null);
  const spread = () => {
    const [a, b] = [...pointers.current.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  };
  const down = (e: RPointerEvent<HTMLDivElement>) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      drag.current = null;
      pinch.current = { d: spread(), view };
      stageRef.current?.setPointerCapture(e.pointerId);
      return;
    }
    if (e.button !== 0) return;
    const g = (e.target as Element).closest("[data-sld-id]") as SVGGElement | null;
    const id = g?.dataset.sldId;
    if (editable && id && ids.has(id)) {
      onSelect(id);
      drag.current = { kind: "el", id, sx: e.clientX, sy: e.clientY, base: { dx: 0, dy: 0 }, view, moved: false };
    } else {
      drag.current = { kind: "pan", sx: e.clientX, sy: e.clientY, base: { dx: 0, dy: 0 }, view, moved: false };
    }
    stageRef.current?.setPointerCapture(e.pointerId);
    stageRef.current?.focus({ preventScroll: true });
  };
  const move = (e: RPointerEvent<HTMLDivElement>) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size === 2) {
      const p0 = pinch.current;
      const rect = stageRef.current!.getBoundingClientRect();
      const [a, b] = [...pointers.current.values()];
      const px = (a!.x + b!.x) / 2 - rect.left;
      const py = (a!.y + b!.y) / 2 - rect.top;
      const z = Math.max(0.5, Math.min(5, (p0.view.z * spread()) / Math.max(1, p0.d)));
      const k = z / p0.view.z;
      setView({ z, x: px - (px - p0.view.x) * k, y: py - (py - p0.view.y) * k });
      return;
    }
    const d = drag.current;
    if (!d) return;
    const mx = e.clientX - d.sx;
    const my = e.clientY - d.sy;
    if (Math.abs(mx) + Math.abs(my) > 3) d.moved = true;
    if (d.kind === "pan") setView({ ...d.view, x: d.view.x + mx, y: d.view.y + my });
    else if (d.id && d.moved) {
      const k = unit();
      d.base = { dx: Math.round((mx / k) * 10) / 10, dy: Math.round((my / k) * 10) / 10 };
      place(d.id, d.base);
    }
  };
  const up = (e: RPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    if (pinch.current) {
      if (pointers.current.size < 2) pinch.current = null;
      drag.current = null;
      return;
    }
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.kind === "el" && d.id && d.moved && (d.base.dx || d.base.dy)) ws.dispatch({ type: "move", id: d.id, dx: d.base.dx, dy: d.base.dy });
    if (d.kind === "pan" && !d.moved) onSelect(null);
  };
  const zoomAt = (factor: number, cx?: number, cy?: number) =>
    setView((v) => {
      const z = Math.max(0.5, Math.min(5, v.z * factor));
      const rect = stageRef.current?.getBoundingClientRect();
      const px = cx != null && rect ? cx - rect.left : (rect?.width ?? 0) / 2;
      const py = cy != null && rect ? cy - rect.top : (rect?.height ?? 0) / 2;
      const k = z / v.z;
      return { z, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
    });
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(e.deltaY > 0 ? 1 / 1.12 : 1.12, e.clientX, e.clientY);
    };
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, []);
  const onKey = (e: RKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && sel) {
      e.stopPropagation();
      return onSelect(null);
    }
    if (!editable || !sel || !e.key.startsWith("Arrow")) return;
    e.preventDefault();
    const step = e.shiftKey ? 10 : 2;
    const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
    const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
    ws.dispatch({ type: "move", id: sel, dx, dy });
  };

  return (
    <div className="pw-sld-stage-wrap" data-anchor="sld">
      <div
        ref={stageRef}
        className={cx("pw-sld-stage", ws.preview.isFetching && "pw-stale")}
        style={{ aspectRatio: `1000 / ${ratioHeight(svg)}` }}
        tabIndex={0}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onKeyDown={onKey}
        aria-label={t("permit.sld.canvas.label")}
      >
        <div className="pw-sld-content" style={{ transform: `translate(${view.x}px, ${view.y}px)`, width: `${view.z * 100}%` }}>
          <div ref={hostRef} className="pw-sld-host" />
        </div>
        {!svg && <div className="pw-sld-empty muted">{t("permit.sld.canvas.drawing")}</div>}
      </div>
      <div className="pw-zoom">
        <button onClick={() => zoomAt(1.25)} aria-label={t("permit.sld.zoomIn")}><Plus size={15} /></button>
        <button onClick={() => zoomAt(1 / 1.25)} aria-label={t("permit.sld.zoomOut")}><Minus size={15} /></button>
        <button onClick={() => setView({ z: 1, x: 0, y: 0 })} className="small">{t("permit.sld.fit")}</button>
      </div>
    </div>
  );
}
